import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ProductCard } from '../components/ProductCard';
import { Crown, Heart, Package, Star, Sparkles, Scissors, Clock, Shield, Truck, Camera } from 'lucide-react';
import { LocalBusinessSchema, BreadcrumbSchema, ProductCollectionSchema, OrganizationSchema, HowToSchema } from '../components/StructuredData';
import { FEATURED_PRODUCTS, INSTAGRAM_FEED_PRODUCTS } from '../data/products';

export default function Home() {
  const breadcrumbs = [
    { name: 'Home', url: 'https://protectyourcrown.store' }
  ];

  return (
    <div data-testid="home-page">
      <LocalBusinessSchema />
      <BreadcrumbSchema items={breadcrumbs} />
      <ProductCollectionSchema />
      <OrganizationSchema />
      <HowToSchema />
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_protect-your-crown/artifacts/9wiwyrj8_CJhome%20page.jpg"
            alt="Christian Clark hand-crocheting a premium hat in Sacramento studio"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <Crown className="w-12 h-12 text-[#A85A43]" strokeWidth={1.5} />
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-[Playfair_Display] text-[#2A2626] tracking-tight leading-[1.1] mb-6">
                Handcrafted Crocheted Hats in Sacramento
              </h1>
              <p className="text-lg sm:text-xl text-[#5C5855] leading-relaxed mb-4 max-w-xl">
                Protect Your Crown is a Sacramento-based artisan crochet brand founded in 2023 by Christian Clark. We hand-crochet premium dreadlock hats, beanie hats, slouchy tams, infinity scarves, and shawls using soft, durable acrylic and cotton-blend yarns.
              </p>
              <p className="text-base text-[#5C5855] leading-relaxed mb-8 max-w-xl">
                Every piece is one-of-a-kind. Our collection of 15+ designs ranges from $30 to $100, with custom orders available. We ship nationwide from Sacramento, CA.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/products"
                  className="bg-[#A85A43] hover:bg-[#8C4935] text-white font-[Manrope] py-4 px-8 rounded-full transition-colors"
                  data-testid="hero-shop-now-button"
                >
                  Shop Collection
                </Link>
                <Link
                  to="/about"
                  className="bg-white hover:bg-[#F9F6F0] text-[#2A2626] font-[Manrope] py-4 px-8 rounded-full border border-[#EADCCC] transition-colors"
                  data-testid="hero-learn-more-button"
                >
                  Our Story
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_protect-your-crown/artifacts/mpj5uhfj_dread%20hat.jpg"
                alt="XL Rasta Dreadlock Tam - handcrafted crocheted hat by Protect Your Crown Sacramento"
                className="w-full rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Hat of the Month */}
      <section className="py-24 bg-gradient-to-br from-[#A85A43]/10 via-[#F9F6F0] to-[#7A8974]/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute -top-6 -left-6 bg-[#A85A43] text-white px-6 py-3 rounded-full font-[Manrope] font-semibold shadow-lg z-10">
                Hat of the Month
              </div>
              <div className="relative overflow-hidden rounded-lg shadow-2xl border-4 border-white">
                <img
                  src="https://customer-assets.emergentagent.com/job_protect-your-crown/artifacts/xu9vhjtt_rasta.jpg"
                  alt="Rasta crocheted dreadlock hat with green yellow and red stripes - Hat of the Month March 2026"
                  className="w-full aspect-square object-cover"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="inline-block bg-[#7A8974]/20 text-[#7A8974] px-4 py-2 rounded-full text-sm font-[Manrope] font-semibold mb-4">
                March 2026 Special
              </div>
              <h2 className="text-4xl sm:text-5xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-6">
                Rasta Crocheted Dreadlock Hat
              </h2>
              <p className="text-lg text-[#5C5855] leading-relaxed mb-6">
                This month's featured hat is a handcrafted Rasta-style dreadlock hat crocheted with soft acrylic yarn in bold green, gold, and red stripes on a rich black foundation. Designed to fit comfortably over dreadlocks or natural hair, this hat takes approximately 6-8 hours to crochet by hand using a single crochet and half-double crochet stitch pattern.
              </p>
              <div className="bg-white border-2 border-[#EADCCC] rounded-lg p-6 mb-8">
                <h3 className="text-xl font-[Playfair_Display] text-[#2A2626] mb-3">
                  Product Details
                </h3>
                <ul className="space-y-3 text-[#5C5855]">
                  <li className="flex items-start gap-3">
                    <span className="text-[#A85A43] mt-1">&#10003;</span>
                    <span><strong>Material:</strong> 100% soft acrylic yarn, machine washable</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#A85A43] mt-1">&#10003;</span>
                    <span><strong>Fit:</strong> Stretchy construction fits most head sizes, extra room for dreads</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#A85A43] mt-1">&#10003;</span>
                    <span><strong>Craft time:</strong> 6-8 hours of hand crochet per hat</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#A85A43] mt-1">&#10003;</span>
                    <span><strong>Made by:</strong> Christian Clark in Sacramento, CA</span>
                  </li>
                </ul>
              </div>
              <div className="flex items-center gap-6 mb-6">
                <span className="text-4xl font-[Playfair_Display] font-semibold text-[#A85A43]">
                  $30
                </span>
                <span className="text-[#5C5855]">Ships nationwide in 3-5 business days</span>
              </div>
              <a
                href="https://a42f7c89-b7c9-4855-94e1-3be02f9ff2b2.paylinks.godaddy.com/dbef2816-058d-448c-863e-d4b"
                className="inline-block bg-[#A85A43] hover:bg-[#8C4935] text-white font-[Manrope] font-semibold py-4 px-10 rounded-full transition-colors shadow-lg hover:shadow-xl"
                data-testid="hat-of-month-buy-button"
              >
                Get This Month's Hat
              </a>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Signature Masterpiece */}
      <section className="py-24 bg-gradient-to-br from-[#2A2626] via-[#3D3636] to-[#2A2626] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-[#D4AF37] fill-[#D4AF37]" />
                <span className="text-[#D4AF37] font-[Manrope] font-semibold text-sm tracking-widest uppercase">
                  Signature Masterpiece
                </span>
                <Star className="w-5 h-5 text-[#D4AF37] fill-[#D4AF37]" />
              </div>
              <h2 className="text-4xl sm:text-5xl font-[Playfair_Display] text-white tracking-tight mb-6">
                Aurora Chevron Shawl
              </h2>
              <p className="text-lg text-[#F9F6F0]/80 leading-relaxed mb-6">
                The Aurora Chevron Shawl is a breathtaking handcrafted crochet shawl featuring over 15 harmonious yarn colors arranged in a classic chevron (zigzag) pattern. Created by Christian Clark using a ripple stitch technique, this piece flows from deep rose and coral through turquoise, gold, and serene blues. Each shawl requires over 40 hours of meticulous hand crochet work using premium soft acrylic yarn.
              </p>
              
              <div className="bg-white/5 backdrop-blur-sm border border-[#D4AF37]/30 rounded-lg p-6 mb-8">
                <h3 className="text-xl font-[Playfair_Display] text-[#D4AF37] mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Product Specifications
                </h3>
                <ul className="space-y-3 text-[#F9F6F0]/90">
                  <li className="flex items-start gap-3">
                    <span className="text-[#D4AF37] mt-1">&#10022;</span>
                    <span><strong>Dimensions:</strong> Approximately 60" x 30" (large wrap size)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#D4AF37] mt-1">&#10022;</span>
                    <span><strong>Material:</strong> Premium soft acrylic yarn in 15+ hand-selected colors</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#D4AF37] mt-1">&#10022;</span>
                    <span><strong>Versatile:</strong> Wear as a wrap, poncho, triangle scarf, or elegant drape</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#D4AF37] mt-1">&#10022;</span>
                    <span><strong>Craft time:</strong> 40+ hours of hand crochet by Christian Clark</span>
                  </li>
                </ul>
              </div>
              
              <div className="flex items-center gap-6 mb-8">
                <div>
                  <span className="text-5xl font-[Playfair_Display] font-semibold text-[#D4AF37]">
                    $100
                  </span>
                  <p className="text-[#F9F6F0]/60 text-sm mt-1">Limited Edition</p>
                </div>
                <div className="h-12 w-px bg-[#D4AF37]/30"></div>
                <div className="text-[#F9F6F0]/80 text-sm">
                  <p>Free Shipping Included</p>
                  <p className="text-[#D4AF37]">Get Yours Today</p>
                </div>
              </div>
              
              <a
                href="https://a42f7c89-b7c9-4855-94e1-3be02f9ff2b2.paylinks.godaddy.com/07aec087-ce87-4a05-8029-d37"
                className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#C9A227] hover:from-[#C9A227] hover:to-[#B8962A] text-[#2A2626] font-[Manrope] font-bold py-4 px-12 rounded-full transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                data-testid="signature-masterpiece-buy-button"
              >
                Claim This Masterpiece
              </a>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative order-1 lg:order-2"
            >
              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-[#D4AF37] to-[#C9A227] text-[#2A2626] px-6 py-3 rounded-full font-[Manrope] font-bold shadow-lg z-10 flex items-center gap-2">
                <Star className="w-4 h-4 fill-current" />
                Premium Collection
              </div>
              <div className="relative overflow-hidden rounded-lg shadow-2xl border-4 border-[#D4AF37]/30">
                <img
                  src="https://customer-assets.emergentagent.com/job_protect-your-crown/artifacts/dyadoihp_shawl.jpg"
                  alt="Aurora Chevron Shawl - handcrafted crochet shawl with 15 colors in chevron zigzag pattern by Christian Clark Sacramento"
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2A2626]/40 to-transparent"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It's Made - Process Section */}
      <section className="py-24 bg-[#F9F6F0]" data-testid="how-its-made-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-4">
              How Our Crocheted Hats Are Made
            </h2>
            <p className="text-lg text-[#5C5855] max-w-3xl mx-auto">
              Every Protect Your Crown hat is hand-crocheted from start to finish in Sacramento, California. Here is our step-by-step process for creating each one-of-a-kind piece.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg p-8 border border-[#EADCCC]"
            >
              <div className="w-12 h-12 bg-[#A85A43]/10 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#A85A43] font-[Manrope] font-bold text-lg">1</span>
              </div>
              <h3 className="text-lg font-[Playfair_Display] text-[#2A2626] mb-3">Yarn Selection</h3>
              <p className="text-[#5C5855] text-sm leading-relaxed">
                Christian hand-selects premium acrylic and cotton-blend yarns for each project. Yarn is chosen for softness, durability, colorfastness, and how it will feel against the skin and hair. We source yarns from trusted U.S. suppliers.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-lg p-8 border border-[#EADCCC]"
            >
              <div className="w-12 h-12 bg-[#A85A43]/10 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#A85A43] font-[Manrope] font-bold text-lg">2</span>
              </div>
              <h3 className="text-lg font-[Playfair_Display] text-[#2A2626] mb-3">Pattern & Design</h3>
              <p className="text-[#5C5855] text-sm leading-relaxed">
                Each hat starts with an original pattern designed by Christian. Stitch techniques include single crochet, half-double crochet, double crochet, and specialty stitches like the ripple stitch used in our Aurora Chevron Shawl. Color placement is planned to create gradients, stripes, or custom patterns.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-lg p-8 border border-[#EADCCC]"
            >
              <div className="w-12 h-12 bg-[#A85A43]/10 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#A85A43] font-[Manrope] font-bold text-lg">3</span>
              </div>
              <h3 className="text-lg font-[Playfair_Display] text-[#2A2626] mb-3">Hand Crochet</h3>
              <p className="text-[#5C5855] text-sm leading-relaxed">
                Using steel and aluminum crochet hooks (sizes H/8 through K/10.5), Christian crochets each piece entirely by hand. A standard beanie takes 4-6 hours; dreadlock tams take 6-8 hours; the Aurora Chevron Shawl takes over 40 hours. No machines are used at any stage.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-lg p-8 border border-[#EADCCC]"
            >
              <div className="w-12 h-12 bg-[#A85A43]/10 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#A85A43] font-[Manrope] font-bold text-lg">4</span>
              </div>
              <h3 className="text-lg font-[Playfair_Display] text-[#2A2626] mb-3">Finishing & Shipping</h3>
              <p className="text-[#5C5855] text-sm leading-relaxed">
                Completed pieces are carefully inspected, loose ends are woven in, and each item is gently steam-blocked to set its shape. Orders ship within 3-5 business days via USPS from Sacramento, CA. The Aurora Chevron Shawl ships free.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-[Playfair_Display] text-[#2A2626] tracking-tight text-center mb-6"
          >
            Why Choose Protect Your Crown?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-lg text-[#5C5855] text-center max-w-3xl mx-auto mb-16"
          >
            Protect Your Crown is Sacramento's dedicated artisan crochet brand, specializing in handcrafted hats for dreadlocks, natural hair, and everyday style since 2023.
          </motion.p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#A85A43]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Scissors className="w-8 h-8 text-[#A85A43]" />
              </div>
              <h3 className="text-lg font-[Playfair_Display] text-[#2A2626] mb-3">100% Handmade</h3>
              <p className="text-[#5C5855] text-sm leading-relaxed">
                Every hat, scarf, and shawl is crocheted entirely by hand. No machines, no mass production. Christian Clark personally crafts each piece in Sacramento.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#A85A43]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-[#A85A43]" />
              </div>
              <h3 className="text-lg font-[Playfair_Display] text-[#2A2626] mb-3">Premium Materials</h3>
              <p className="text-[#5C5855] text-sm leading-relaxed">
                We use soft acrylic and cotton-blend yarns that are hypoallergenic, machine washable, and built to last. Colors stay vibrant wash after wash.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#A85A43]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Crown className="w-8 h-8 text-[#A85A43]" />
              </div>
              <h3 className="text-lg font-[Playfair_Display] text-[#2A2626] mb-3">Dreadlock Specialists</h3>
              <p className="text-[#5C5855] text-sm leading-relaxed">
                Our dreadlock hats and oversized tams are designed with extra room and stretch to comfortably fit over locs, braids, twists, and natural hair without snagging.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#A85A43]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Truck className="w-8 h-8 text-[#A85A43]" />
              </div>
              <h3 className="text-lg font-[Playfair_Display] text-[#2A2626] mb-3">Nationwide Shipping</h3>
              <p className="text-[#5C5855] text-sm leading-relaxed">
                Based in Sacramento, we ship all orders via USPS within 3-5 business days. The Aurora Chevron Shawl includes free shipping. Custom orders ship in 7-10 days.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Materials & Care Quick Reference */}
      <section className="py-16 bg-[#2A2626]" data-testid="materials-care-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-[Playfair_Display] text-white mb-4">Materials We Use</h3>
              <ul className="space-y-3 text-[#F9F6F0]/80 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#A85A43] mt-0.5">&#8226;</span>
                  <span><strong className="text-white">Soft Acrylic Yarn:</strong> Lightweight, hypoallergenic, and machine washable. Used in most of our hats and the Aurora Chevron Shawl.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#A85A43] mt-0.5">&#8226;</span>
                  <span><strong className="text-white">Cotton-Blend Yarn:</strong> Breathable and ideal for warmer weather. Used in summer hats and lighter-weight designs.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#A85A43] mt-0.5">&#8226;</span>
                  <span><strong className="text-white">Crochet Hooks:</strong> Aluminum and steel hooks in sizes H/8 to K/10.5, selected based on yarn weight and desired stitch density.</span>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-[Playfair_Display] text-white mb-4">Care Instructions</h3>
              <ul className="space-y-3 text-[#F9F6F0]/80 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#A85A43] mt-0.5">&#8226;</span>
                  <span><strong className="text-white">Washing:</strong> Machine wash on gentle/delicate cycle with cold water, or hand wash. Use mild detergent.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#A85A43] mt-0.5">&#8226;</span>
                  <span><strong className="text-white">Drying:</strong> Lay flat to dry to maintain shape. Do not wring or twist. Avoid tumble drying on high heat.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#A85A43] mt-0.5">&#8226;</span>
                  <span><strong className="text-white">Storage:</strong> Store flat or loosely rolled. Avoid hanging on hooks for extended periods, which can stretch the fabric.</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Featured Products */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-4">
              Featured Collection
            </h2>
            <p className="text-lg text-[#5C5855] max-w-2xl mx-auto">
              Handpicked favorites from our collection of 16 crocheted hats, scarves, rugs, and shawls. Prices range from $30 to $100.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {FEATURED_PRODUCTS.map((product, index) => (
              <ProductCard key={product.name} {...product} index={index} />
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-block bg-[#A85A43] hover:bg-[#8C4935] text-white font-[Manrope] py-4 px-8 rounded-full transition-colors"
              data-testid="view-all-products-button"
            >
              View All 16 Products
            </Link>
          </div>
        </div>
      </section>
      
      {/* Brand Story Teaser */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-6">
              About Christian Clark & Protect Your Crown
            </h2>
            <p className="text-lg text-[#5C5855] leading-relaxed mb-4">
              Protect Your Crown was founded in 2023 by Christian Clark, a self-taught crocheter based in Sacramento, California. What began as a creative outlet grew into Sacramento's go-to source for artisan crocheted hats, dreadlock tams, infinity scarves, and shawls.
            </p>
            <p className="text-base text-[#5C5855] leading-relaxed mb-8">
              Christian specializes in dreadlock-friendly hat designs that accommodate locs, braids, and natural hair with comfort and style. Every item is hand-crocheted using premium yarns and original patterns. The collection now includes 15 unique designs priced from $30 to $100, plus fully custom orders.
            </p>
            <Link
              to="/about"
              className="inline-block text-[#A85A43] hover:text-[#8C4935] font-[Manrope] font-semibold transition-colors"
              data-testid="read-our-story-link"
            >
              Read Our Full Story &rarr;
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* Local SEO Section */}
      <section className="py-16 bg-[#F9F6F0]">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-4 text-center">
              Handcrafted in Sacramento, California
            </h2>
            <p className="text-[#5C5855] text-center max-w-2xl mx-auto mb-10">
              Protect Your Crown serves Sacramento and ships nationwide. We are a Black-owned small business creating handmade crochet goods since 2023.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
              <div className="bg-white p-6 rounded-lg border border-[#EADCCC]">
                <h3 className="text-xl font-[Playfair_Display] text-[#A85A43] mb-3">Dreadlock Hats Sacramento</h3>
                <p className="text-[#5C5855] leading-relaxed text-sm">
                  Our dreadlock hats are specifically designed with extra depth and stretch to comfortably cover locs, twists, braids, and natural hair. Popular styles include the Rasta ($30) and the XL Rasta Dreadlock Tam ($60). Made with soft acrylic yarn that won't snag or pull hair. Available for purchase online with shipping from Sacramento.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-[#EADCCC]">
                <h3 className="text-xl font-[Playfair_Display] text-[#A85A43] mb-3">Beanie Hats Sacramento</h3>
                <p className="text-[#5C5855] leading-relaxed text-sm">
                  Cozy crocheted beanie hats for Sacramento's cooler months, starting at $30. Choose from vibrant patterns like Multi-color Vibin', gradient designs like Ocean Breeze, or solid-color classics like Soul-shine. Each beanie is hand-crocheted in 4-6 hours using premium yarn and fits most adult head sizes.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-[#EADCCC]">
                <h3 className="text-xl font-[Playfair_Display] text-[#A85A43] mb-3">Custom Crochet Orders</h3>
                <p className="text-[#5C5855] leading-relaxed text-sm">
                  Want something unique? We accept custom crochet orders starting at $30. Choose your style (beanie, dreadlock hat, tam, scarf), preferred colors, and size. Custom orders are completed within 5-10 business days and ship via USPS. Submit your request through our Products page or email info@protectmycrown.com.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Instagram-Style Product Gallery */}
      <section className="py-24 bg-white" data-testid="instagram-feed-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Camera className="w-6 h-6 text-[#A85A43]" />
              <span className="text-sm font-[Manrope] font-semibold text-[#A85A43] tracking-widest uppercase">@protectyourcrown</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-4">
              From Our Studio
            </h2>
            <p className="text-lg text-[#5C5855]">
              A look at our handcrafted collection, fresh from Christian's crochet hooks
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {INSTAGRAM_FEED_PRODUCTS.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to="/products" className="group block relative aspect-square overflow-hidden rounded-lg">
                  <img
                    src={item.image}
                    alt={`${item.name} - handcrafted by Protect Your Crown Sacramento`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-[#2A2626]/0 group-hover:bg-[#2A2626]/60 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center text-white px-4">
                      <p className="font-[Playfair_Display] text-lg">{item.name}</p>
                      <p className="font-[Manrope] font-semibold text-[#D4AF37]">${item.price}</p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/products"
              className="inline-block bg-[#A85A43] hover:bg-[#8C4935] text-white font-[Manrope] py-3 px-8 rounded-full transition-colors"
              data-testid="instagram-feed-shop-button"
            >
              Shop All 16 Products
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-16 bg-[#F9F6F0]" data-testid="faq-teaser-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-[Playfair_Display] text-[#2A2626] tracking-tight mb-4">
              Have Questions?
            </h2>
            <p className="text-[#5C5855] leading-relaxed mb-8 max-w-xl mx-auto">
              Check out our FAQ page for answers about pricing, materials, shipping, care instructions, custom orders, and more.
            </p>
            <Link
              to="/faq"
              className="inline-block bg-[#A85A43] hover:bg-[#8C4935] text-white font-[Manrope] py-3 px-8 rounded-full transition-colors"
              data-testid="faq-teaser-button"
            >
              View All FAQs
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
